import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Global pending-request counter (drives the top loading bar)
let pending = 0;
const listeners = new Set();
export const onPendingChange = (fn) => { listeners.add(fn); fn(pending); return () => listeners.delete(fn); };
const bump = (d) => { pending = Math.max(0, pending + d); listeners.forEach((fn) => fn(pending)); };

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const RETRY_DELAYS = [600, 1500, 3000];
const retryable = (err) => err.code !== "ECONNABORTED" && err.code !== "ERR_CANCELED" && (!err.response || err.response.status >= 500 || err.response.status === 429);

api.interceptors.request.use((cfg) => { bump(1); return cfg; });
api.interceptors.response.use(
  (res) => { bump(-1); return res; },
  async (err) => {
    bump(-1);
    const cfg = err.config || {};
    const n = cfg.__retry || 0;
    if ((cfg.method || "get").toLowerCase() === "get" && retryable(err) && n < RETRY_DELAYS.length) {
      await sleep(RETRY_DELAYS[n]);
      return api({ ...cfg, __retry: n + 1 });
    }
    return Promise.reject(err);
  },
);

export const coverUrl = (path, w = 480) => (path ? (w ? `${API}/cover/${path}?w=${w}` : `${API}/cover/${path}`) : null);
export const streamUrl = (id) => `${API}/stream/track/${id}`;
export const downloadTrackUrl = (id) => `${API}/download/track/${id}`;
export const downloadPackUrl = (id) => `${API}/download/pack/${id}`;
export const downloadPaidUrl = (session_id) => `${API}/download/paid/${session_id}`;
export const streamPackTrackUrl = (packId, trackId) => `${API}/stream/pack/${packId}/${trackId}`;
export const downloadPackTrackUrl = (packId, trackId, orderId) =>
  `${API}/download/pack/${packId}/track/${trackId}${orderId ? `?order_id=${orderId}` : ""}`;
export const trackSrc = (t) => t.stream_url || streamUrl(t.id);

export const splitPistaTitle = (pt) => {
  if (pt.artist) return { title: pt.title, artist: pt.artist };
  const m = /^(.+?)\s+[-–]\s+(.+)$/.exec(pt.title || "");
  return m ? { title: m[2].trim(), artist: m[1].trim() } : { title: pt.title, artist: "" };
};

export const DEFAULT_TRACK_PRICE = 0.99;
export const pistaPrice = (pack, pt) => (Number(pack.price) === 0 ? 0 : (pt.track_price ?? DEFAULT_TRACK_PRICE));
export const buyItemFor = (t) => (t?.is_pista && !t.track_id ? { item: t, itemType: "pista" } : { item: t, itemType: "track" });

export const packTrackToPlayable = (pack, pt) => ({
  id: pt.id,
  title: splitPistaTitle(pt).title,
  artist: splitPistaTitle(pt).artist || "",
  price: pistaPrice(pack, pt),
  pack_price: pack.price,
  track_price: pt.track_price ?? null,
  bpm: pt.bpm || null,
  music_key: pt.music_key || null,
  tags: pt.tags || [],
  duration: pt.duration || null,
  preview_start: pt.preview_start || 0,
  cover_path: pt.cover_path || pack.cover_path,
  genre: pt.genre || null,
  highlight_genres: pt.highlight_genres || [],
  created_at: pt.created_at || pack.created_at,
  is_pista: true,
  pack_id: pack.id,
  pack_title: pack.title,
  pack_tracks: (pack.tracks || []).length,
  track_id: pt.track_id || null,
  versions: pt.versions || [],
  original_filename: pt.original_filename || null,
  is_new: !!pt.is_new,
  category: pt.category || (/mashup/i.test(pt.title || "") ? "mashup" : /remix/i.test(pt.title || "") ? "remix" : "pool"),
  coming_soon: !!pack.coming_soon,
  stream_url: streamPackTrackUrl(pack.id, pt.id),
  download_url: downloadPackTrackUrl(pack.id, pt.id),
});

export const fmtDuration = (s) => {
  if (!s || !Number.isFinite(s)) return "";
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
};

export const queueAfter = (list, id) => {
  const i = list.findIndex((t) => t.id === id);
  return i < 0 ? list : [...list.slice(i + 1), ...list.slice(0, i)];
};

export const streamVersionUrl = (trackId, vId) => `${API}/stream/track/${trackId}/version/${vId}`;
export const downloadVersionUrl = (trackId, vId) => `${API}/download/track/${trackId}/version/${vId}`;

export const detailPathFor = (t) => (t.is_pista && !t.track_id ? `/pista/${t.id}` : `/track/${t.track_id || t.id}`);
export const SECTION_LABELS = { remix: "Remix", mashup: "Mashup", original: "Original", pool: "Pool" };
export const sectionOf = (t) => t.category || (/mashup/i.test(t.title || "") ? "mashup" : /remix/i.test(t.title || "") ? "remix" : "pool");

export const stripArtist = (title, artist) => {
  const t = (title || "").trim();
  const m = /^(.+?)\s+[-–]\s+(.+)$/.exec(t);
  if (!m) return t;
  const left = m[1].trim().toLowerCase();
  const art = (artist || "").toLowerCase();
  if (!art || art.includes(left) || left.includes(art.split(/\s*[,&x]\s*/i)[0].trim())) return m[2].trim();
  return t;
};

export const trackVersionsPlayable = (track) => [
  { ...track, version_title: stripArtist((track.original_filename || track.title).replace(/\.[^.]+$/, ""), track.artist), track_id: track.is_pista ? track.track_id : track.id, version_id: null, download_url: track.download_url || downloadTrackUrl(track.id) },
  ...(track.versions || []).map((v) => ({
    id: v.id,
    title: `${track.title} (${v.title})`,
    version_title: stripArtist((v.original_filename || v.title).replace(/\.[^.]+$/, ""), v.artist || track.artist),
    artist: v.artist || track.artist,
    price: v.price != null ? v.price : track.price,
    version_id: v.id,
    bpm: track.bpm,
    tags: track.tags || [],
    cover_path: track.cover_path,
    duration: v.duration || null,
    preview_start: track.preview_start || 0,
    track_id: track.is_pista ? track.track_id : track.id,
    is_pista: !!track.is_pista, pack_id: track.pack_id, pack_title: track.pack_title, pack_price: track.pack_price, pack_tracks: track.pack_tracks, pista_id: track.is_pista ? track.id : null,
    stream_url: track.is_pista ? `${API}/stream/pack/${track.pack_id}/${track.id}/version/${v.id}` : streamVersionUrl(track.id, v.id),
    download_url: track.is_pista ? `${API}/download/pack/${track.pack_id}/track/${track.id}/version/${v.id}` : downloadVersionUrl(track.id, v.id),
  })),
];

async function chunkedUpload(file, { folder = "packs", contentType, onProgress, chunkSize = 8 * 1024 * 1024 } = {}) {
  const uploadId = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  const total = Math.ceil(file.size / chunkSize);
  for (let i = 0; i < total; i++) {
    const fd = new FormData();
    fd.append("upload_id", uploadId);
    fd.append("index", String(i));
    fd.append("chunk", file.slice(i * chunkSize, Math.min(file.size, (i + 1) * chunkSize)), `${i}.part`);
    let lastErr = null;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        await api.post("/admin/uploads/chunk", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (ev) => ev.total && onProgress?.(Math.round(((i + ev.loaded / ev.total) / total) * 100)),
        });
        lastErr = null;
        break;
      } catch (e) { lastErr = e; await sleep(1000 * (attempt + 1)); }
    }
    if (lastErr) throw lastErr;
    onProgress?.(Math.round(((i + 1) / total) * 100));
  }
  const done = new FormData();
  done.append("upload_id", uploadId);
  done.append("filename", file.name);
  done.append("folder", folder);
  done.append("content_type", contentType || file.type || "application/octet-stream");
  const res = await api.post("/admin/uploads/complete", done, { headers: { "Content-Type": "multipart/form-data" } });
  return res.data;
}

export async function uploadFileChunked(file, opts = {}) {
  return (await chunkedUpload(file, opts)).path;
}

// Uploads an audio file in chunks; resolves {path, size, duration, cover_path}
export function uploadAudioChunked(file, { folder = "audio", onProgress } = {}) {
  return chunkedUpload(file, { folder, contentType: file.type || "audio/mpeg", onProgress });
}

// Ask the server to pre-fill its audio cache for the tracks on screen (cheap: no audio reaches the browser)
const warmed = new Set();
export function warmVisible(tracks) {
  const ids = (tracks || []).map((t) => t?.id).filter((id) => id && !warmed.has(id)).slice(0, 8);
  if (!ids.length) return;
  ids.forEach((id) => warmed.add(id));
  api.post("/warm", { track_ids: ids }).catch(() => {});
}
